/**
 * Start Stripe.js Elements as soon as the buyer is on a listing or the
 * checkout route, so the card iframe is already loading before first paint
 * of StripePaymentForm.
 */

export const stripeElementsOptions = {
  fonts: [
    {
      cssSrc: 'https://fonts.googleapis.com/css?family=Inter',
    },
  ],
};

export const getStripeCardStyles = () => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  return {
    base: {
      fontFamily:
        "'DM Sans', -apple-system, BlinkMacSystemFont, 'Inter', Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
      fontSize: isMobile ? '14px' : '16px',
      fontSmoothing: 'antialiased',
      lineHeight: '24px',
      letterSpacing: '-0.1px',
      color: '#fff',
      '::placeholder': {
        color: '#B2B2B2',
      },
    },
  };
};

let warmed = null;

const ensureWarmupHost = () => {
  if (typeof document === 'undefined') {
    return null;
  }
  const existing = document.getElementById('sw-stripe-card-warmup');
  if (existing) {
    return existing;
  }
  const host = document.createElement('div');
  host.id = 'sw-stripe-card-warmup';
  host.setAttribute('aria-hidden', 'true');
  host.setAttribute('data-testid', 'stripe-card-warmup');
  host.style.cssText =
    'position:fixed;left:-10000px;top:0;width:320px;height:40px;overflow:hidden;pointer-events:none;';
  document.body.appendChild(host);
  return host;
};

export const warmStripe = publishableKey => {
  if (typeof window === 'undefined' || !window.Stripe || !publishableKey) {
    return null;
  }
  if (warmed && warmed.key === publishableKey && warmed.stripe) {
    return warmed;
  }
  try {
    const stripe = window.Stripe(publishableKey);
    const elements = stripe.elements(stripeElementsOptions);
    const card = elements.create('card', { style: getStripeCardStyles() });
    const host = ensureWarmupHost();
    if (host) {
      card.mount(host);
    }
    warmed = { key: publishableKey, stripe, elements, card, host };
    return warmed;
  } catch (e) {
    return null;
  }
};

export const getWarmedStripe = publishableKey => {
  if (warmed && warmed.key === publishableKey) {
    return warmed;
  }
  return warmStripe(publishableKey);
};

const revealWarmupHost = mountNode => {
  if (!warmed?.host || !mountNode) {
    return false;
  }
  warmed.host.style.cssText =
    'width:100%;min-height:24px;position:relative;left:auto;top:auto;overflow:visible;pointer-events:auto;';
  if (warmed.host.parentNode !== mountNode) {
    mountNode.appendChild(warmed.host);
  }
  return true;
};

/**
 * Move the already-mounted warmup card into the visible checkout host
 * without unmount/remount (that reloads the Stripe iframe ~1s).
 */
export const adoptWarmedCard = mountNode => {
  if (!warmed?.card || !mountNode) {
    return null;
  }
  if (revealWarmupHost(mountNode)) {
    warmed.adopted = true;
    return warmed;
  }
  return warmed;
};

export const warmStripeWhenReady = publishableKey => {
  const ready = warmStripe(publishableKey);
  if (ready || typeof window === 'undefined' || !publishableKey) {
    return ready;
  }
  const started = Date.now();
  const tick = () => {
    if (warmStripe(publishableKey) || Date.now() - started > 8000) {
      return;
    }
    window.setTimeout(tick, 50);
  };
  window.setTimeout(tick, 0);
  return null;
};

export const isCheckoutPath = (pathname = '') => /\/checkout(?:\/|$)/.test(pathname || '');

/**
 * Cold /checkout: start Stripe.js + the checkout chunk as soon as the
 * main bundle runs, so the card host exists before CheckoutPage paints.
 */
export const warmCheckoutAtAppStart = (
  publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
) => {
  if (typeof window === 'undefined') {
    return null;
  }
  if (!isCheckoutPath(window.location?.pathname || '')) {
    return null;
  }
  prefetchCheckoutChunk();
  return warmStripeWhenReady(publishableKey);
};

export const prefetchCheckoutChunk = () => {
  if (typeof window === 'undefined' || prefetchCheckoutChunk.started) {
    return;
  }
  prefetchCheckoutChunk.started = true;
  if (process.env.NODE_ENV === 'test') {
    return;
  }
  import(/* webpackChunkName: "CheckoutPage" */ '../containers/CheckoutPage/CheckoutPage');
};

export const resetStripeWarmupForTests = () => {
  warmed = null;
  prefetchCheckoutChunk.started = false;
};
